// Carrier brand colours for the quote-list badges (src/wizard/steps/
// RatesStep.tsx). Different providers slug the same carrier differently —
// Parcel2Go uses "royalmail"/"hermes", the mock and Royal Mail direct
// providers use "royal_mail" (see shared/providers/mock-provider.ts,
// shared/providers/royal-mail-provider.ts) — so matching strips every
// separator (spaces, hyphens, underscores, "+") before comparing, and the
// match patterns below are written in that same separator-free form.
export interface CarrierBrand {
  color: string;
  textColor: string;
  initials: string;
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/[\s_\-+]/g, "");
}

const BRANDS: { match: string[]; brand: CarrierBrand }[] = [
  { match: ["royalmail"], brand: { color: "#DC0032", textColor: "#ffffff", initials: "RM" } },
  { match: ["hermes", "myhermes", "evri"], brand: { color: "#672780", textColor: "#ffffff", initials: "Ev" } },
  { match: ["dpd"], brand: { color: "#DC0032", textColor: "#ffffff", initials: "DPD" } },
  { match: ["ups"], brand: { color: "#351C15", textColor: "#FFB500", initials: "UPS" } },
  { match: ["parcelforce"], brand: { color: "#1B3E7C", textColor: "#ffffff", initials: "PF" } },
  { match: ["inpost"], brand: { color: "#FFCB05", textColor: "#1a1a1a", initials: "IP" } },
  { match: ["yodel", "collectplus"], brand: { color: "#6E2585", textColor: "#ffffff", initials: "Yo" } },
  { match: ["dx"], brand: { color: "#00205B", textColor: "#ffffff", initials: "DX" } },
  { match: ["ukmail"], brand: { color: "#0072CE", textColor: "#ffffff", initials: "UK" } },
  { match: ["tnt"], brand: { color: "#FF6200", textColor: "#ffffff", initials: "TNT" } },
  { match: ["dhl"], brand: { color: "#FFCC00", textColor: "#D40511", initials: "DHL" } },
  { match: ["fedex"], brand: { color: "#4D148C", textColor: "#FF6200", initials: "Fx" } },
];

const FALLBACK: CarrierBrand = { color: "#55636e", textColor: "#ffffff", initials: "?" };

export function getCarrierBrand(carrierSlugOrName: string): CarrierBrand {
  const key = normalize(carrierSlugOrName);
  const found = BRANDS.find((b) => b.match.some((m) => key.includes(m)));
  return found?.brand ?? { ...FALLBACK, initials: carrierSlugOrName.replace(/[^a-zA-Z]/g, "").slice(0, 2).toUpperCase() || "?" };
}

// Self-hosted logo assets (public/carrier-logos/*) keyed the same way as
// BRANDS above. Royal Mail/Evri/Yodel/InPost sourced from Wikimedia Commons
// (each tagged PD-textlogo — simple enough to not meet the threshold of
// originality for copyright — though the marks themselves remain
// trademarks of their respective owners, used here only to identify real
// carrier options, not to imply endorsement); DHL/DPD/FedEx/UPS from Simple
// Icons (CC0, simpleicons.org). Returns undefined — falling back to the
// colour-coded initials badge — for any carrier without a sourced logo
// file (e.g. Parcelforce: no usable source found).
const LOGO_FILES: { match: string[]; file: string }[] = [
  { match: ["royalmail"], file: "royal-mail.svg" },
  { match: ["hermes", "myhermes", "evri"], file: "evri.png" },
  { match: ["yodel"], file: "yodel.svg" },
  { match: ["inpost"], file: "inpost.svg" },
  { match: ["dhl"], file: "dhl.svg" },
  { match: ["dpd"], file: "dpd.svg" },
  { match: ["fedex"], file: "fedex.svg" },
  { match: ["ups"], file: "ups.svg" },
  { match: ["collectplus"], file: "yodel.svg" },
];

export function carrierLogoSrc(carrierSlugOrName: string): string | undefined {
  const key = normalize(carrierSlugOrName);
  const found = LOGO_FILES.find((b) => b.match.some((m) => key.includes(m)));
  return found ? `/carrier-logos/${found.file}` : undefined;
}

/**
 * Rebrands stale carrier text — Parcel2Go's sandbox catalogue is confirmed
 * legacy fixture data (see docs/parcel2go-payment-validation.md): Hermes
 * rebranded to Evri in March 2022, and the "collectplus" slug now carries
 * Yodel's branding on Parcel2Go's live catalogue. This only rewrites
 * DISPLAY text — the carrier/service slugs used for actual quotes/orders
 * are never touched, so it's a presentation fix, not a change to which
 * carriers/services are offered or how they're addressed via the API.
 */
const REBRANDS: { carrierMatch: string[]; from: RegExp[]; to: string }[] = [
  { carrierMatch: ["hermes", "myhermes"], from: [/my\s*hermes/gi, /hermes/gi], to: "Evri" },
  { carrierMatch: ["collectplus"], from: [/collect\s*\+?/gi], to: "Yodel" },
];

export function presentableCarrierText(carrierSlugOrName: string, text: string): string {
  const key = normalize(carrierSlugOrName);
  const rebrand = REBRANDS.find((r) => r.carrierMatch.some((m) => key.includes(m)));
  if (!rebrand) return text;
  let result = text;
  for (const pattern of rebrand.from) result = result.replace(pattern, rebrand.to);
  return result;
}
